import gql from 'graphql-tag';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';

import type { CreateContentInlineMutation } from './__types__/CreateWhiteboardMutation.ts';
import { getFullWhiteboardUrl } from './urls.ts';

type CreatedWhiteboardData = {
	contentId: string;
	fullUrl: string;
};

export async function createWhiteboardOverGraphQL(
	pageId: string,
	spaceId: string,
	spaceKey: string,
	templateKey: string,
	createAnalyticsEvent: CreateUIAnalyticsEvent | undefined,
): Promise<CreatedWhiteboardData> {
	// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
	const mutationResult = await getApolloClient().mutate<CreateContentInlineMutation>({
		// eslint-disable-next-line graphql-relay-compat/consistent-graphql-operation-naming -- The naming is actually fine, but the eslint rule mis-fires due to dash in the filename
		mutation: gql`
			mutation CreateWhiteboardMutation($createdInContentId: ID!, $spaceKey: String) {
				createContentInline(
					input: {
						createdInContentId: $createdInContentId
						spaceKey: $spaceKey
						type: "whiteboard"
					}
				) {
					id
				}
			}
		`,
		variables: { createdInContentId: pageId, spaceKey },
	});

	const contentId = mutationResult.data?.createContentInline.id;
	if (!contentId) {
		throw Error('no content id present');
	}

	// track event - whiteboard created inline
	const event = createAnalyticsEvent?.({
		type: 'sendTrackEvent',
		data: {
			action: 'createdByFlow',
			actionSubject: 'content',
			objectType: 'whiteboard',
			objectId: contentId,
			containerType: 'space',
			containerId: spaceId,
			source: 'createContentHandler',
			attributes: {
				flow: 'inlineWhiteboardCreation',
				contentType: 'whiteboard',
				collabService: 'canvas-relay',
				templateKey,
			},
		},
	});
	event?.fire();

	// return full URL so user can navigate
	return { contentId, fullUrl: getFullWhiteboardUrl(contentId, spaceKey) };
}
